// packages/activity/src/routes/table.tsx
import { get, ref, remove, update } from "firebase/database";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomizationPanel from "../components/CustomizationPanel.js";
import LogFeed from "../components/LogFeed.js";
import RosterList from "../components/RosterList.js";
import SceneBoard from "../components/SceneBoard.js";
import SceneTransitionDialog from "../components/SceneTransitionDialog.js";
import UpdateBanner, { type UpdateEntry } from "../components/UpdateBanner.js";
import UpdateDiffModal, { type DiffFetchInput } from "../components/UpdateDiffModal.js";
import WizardModal from "../components/WizardModal.js";
import { getFirebase } from "../firebase/init.js";
import { useRtdbValue } from "../firebase/listeners.js";
import { fetchPublicPack } from "../lib/pack-fetch.js";
import { type WizardStateRecord, isWizardComplete } from "../lib/wizard-state.js";
import { useAuthStore } from "../stores/auth.js";
import { useTableStore } from "../stores/table.js";

interface TableMemberEntry {
  role: "gm" | "gm_assistant" | "pc" | "audience";
  characterIds?: string[];
}

interface TableMetaLive {
  members?: Record<string, TableMemberEntry>;
  wizardState?: WizardStateRecord;
  guildId?: string;
  rulesetPackRef?: {
    id: string;
    version: string;
    source: "public" | "guild" | "user";
    snapshot: string;
  };
  scenePackRefs?: Record<string, { version: string; source: string; snapshot: string }>;
  updateAvailable?: Record<string, string>;
}

function versionPathKey(v: string): string {
  return v.replace(/\./g, "_");
}

function buildEntries(meta: TableMetaLive | null): UpdateEntry[] {
  const notices = meta?.updateAvailable ?? {};
  const out: UpdateEntry[] = [];
  for (const [key, latest] of Object.entries(notices)) {
    const [kind, ...rest] = key.split(":");
    const packId = rest.join(":");
    if (kind === "ruleset") {
      const ref = meta?.rulesetPackRef;
      if (ref && ref.id === packId) {
        out.push({ key, kind, packId, currentVersion: ref.version, latestVersion: latest });
      }
    } else if (kind === "scene") {
      const ref = meta?.scenePackRefs?.[packId];
      if (ref) {
        out.push({
          key,
          kind,
          packId,
          currentVersion: ref.version,
          latestVersion: latest,
        });
      }
    }
  }
  return out;
}

export default function Table(): ReactElement {
  const { t } = useTranslation();
  const { tableId, meta } = useTableStore((s) => ({ tableId: s.tableId, meta: s.meta }));
  const uid = useAuthStore((s) => s.uid);
  const { data: metaLive } = useRtdbValue<TableMetaLive>(tableId ? `tables/${tableId}/meta` : null);
  const { data: scenesLive } = useRtdbValue<Record<string, { status: string }>>(
    tableId ? `tables/${tableId}/scenes` : null,
  );
  const isGM = uid === meta?.gmUid;
  const [openEntry, setOpenEntry] = useState<UpdateEntry | null>(null);
  const [transitionOpen, setTransitionOpen] = useState<boolean>(false);
  const [customizationOpen, setCustomizationOpen] = useState<boolean>(false);
  const [wizardDismissed, setWizardDismissed] = useState<boolean>(false);

  const entries = useMemo(() => buildEntries(metaLive), [metaLive]);
  const activeSceneId = useMemo<string | null>(() => {
    if (!scenesLive) return null;
    for (const [id, scene] of Object.entries(scenesLive)) {
      if (scene?.status === "active") return id;
    }
    return null;
  }, [scenesLive]);

  const rulesetRef = metaLive?.rulesetPackRef;
  const installedScenePackIds = useMemo(
    () => Object.keys(metaLive?.scenePackRefs ?? {}),
    [metaLive?.scenePackRefs],
  );

  const rulesetPackPath = rulesetRef
    ? `packs/${
        rulesetRef.source === "guild"
          ? `guilds/${metaLive?.guildId ?? ""}`
          : rulesetRef.source === "user"
            ? `users/${uid ?? ""}`
            : "public"
      }/ruleset/${rulesetRef.id}/${versionPathKey(rulesetRef.version)}`
    : null;

  const { data: rulesetPack } = useRtdbValue<{
    content?: { skills?: Array<{ id: string }> };
  }>(rulesetPackPath);

  const packSkillIds = useMemo(
    () => (rulesetPack?.content?.skills ?? []).map((s) => s.id),
    [rulesetPack?.content?.skills],
  );

  const { data: premise } = useRtdbValue<{ titleKey?: string; descKey?: string }>(
    tableId ? `tables/${tableId}/setting/premise` : null,
  );

  const wizardAutoOpen = isGM && !wizardDismissed && !isWizardComplete(metaLive?.wizardState);

  if (!tableId || !meta) return <p className="p-4">{t("app.loading")}</p>;

  async function dismiss(entry: UpdateEntry): Promise<void> {
    const { db } = getFirebase();
    await remove(ref(db, `tables/${tableId}/meta/updateAvailable/${entry.key}`));
  }

  async function accept(entry: UpdateEntry): Promise<void> {
    const { db } = getFirebase();
    const newSnap = await get(
      ref(db, `packs/public/${entry.kind}/${entry.packId}/${versionPathKey(entry.latestVersion)}`),
    );
    const newPack = newSnap.val() as { snapshot?: string } | null;
    if (!newPack) throw new Error("upstream pack missing");

    const updates: Record<string, unknown> = {};
    const metaPath = `tables/${tableId}/meta`;
    if (entry.kind === "ruleset") {
      updates[`${metaPath}/rulesetPackRef/version`] = entry.latestVersion;
      updates[`${metaPath}/rulesetPackRef/snapshot`] = newPack.snapshot ?? entry.latestVersion;
    } else {
      updates[`${metaPath}/scenePackRefs/${entry.packId}/version`] = entry.latestVersion;
      updates[`${metaPath}/scenePackRefs/${entry.packId}/snapshot`] =
        newPack.snapshot ?? entry.latestVersion;
    }
    updates[`${metaPath}/updateAvailable/${entry.key}`] = null;
    await update(ref(db), updates);
    setOpenEntry(null);
  }

  const fetchPack = async (input: DiffFetchInput) =>
    fetchPublicPack(input.kind, input.packId, input.version);

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4">
      <header>
        <h1 className="text-2xl font-bold">{meta.name}</h1>
      </header>

      <UpdateBanner isGM={isGM} entries={entries} onView={setOpenEntry} onDismiss={dismiss} />

      {openEntry && (
        <UpdateDiffModal
          kind={openEntry.kind}
          packId={openEntry.packId}
          fromVersion={openEntry.currentVersion}
          toVersion={openEntry.latestVersion}
          fetchPack={fetchPack}
          onAccept={() => accept(openEntry)}
          onCancel={() => setOpenEntry(null)}
        />
      )}

      {isGM && (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded border px-3 py-1 text-xs"
            onClick={() => setCustomizationOpen(true)}
          >
            {t("customization.open")}
          </button>
          <button
            type="button"
            className="rounded border px-3 py-1 text-xs"
            onClick={() => setTransitionOpen(true)}
          >
            {t("edit.transition.open")}
          </button>
        </div>
      )}

      <SceneBoard tableId={tableId} isGM={isGM} />

      <SceneTransitionDialog
        open={transitionOpen}
        tableId={tableId}
        activeSceneId={activeSceneId}
        onClose={() => setTransitionOpen(false)}
      />

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">
            {t("table.roster")}
          </h2>
          <div className="mt-1">
            {metaLive?.members ? <RosterList members={metaLive.members} /> : null}
          </div>
        </div>
        <div>
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">
            {t("table.log")}
          </h2>
          <div className="mt-1 max-h-80 overflow-y-auto">
            <LogFeed tableId={tableId} />
          </div>
        </div>
      </section>

      <WizardModal
        open={wizardAutoOpen}
        tableId={tableId}
        uid={uid ?? undefined}
        guildId={metaLive?.guildId}
        wizardState={metaLive?.wizardState}
        currentRulesetId={rulesetRef?.id ?? null}
        currentRulesetVersion={rulesetRef?.version ?? null}
        installedScenePackIds={installedScenePackIds}
        {...(premise?.titleKey ? { initialPremiseTitle: premise.titleKey } : {})}
        {...(premise?.descKey ? { initialPremiseDesc: premise.descKey } : {})}
        onClose={() => setWizardDismissed(true)}
      />

      <CustomizationPanel
        open={customizationOpen}
        tableId={tableId}
        packSkillIds={packSkillIds}
        onClose={() => setCustomizationOpen(false)}
      />
    </div>
  );
}
